<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useRoute } from 'vue-router';
import orderIssuesAPI from 'dashboard/api/orderIssues';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import OrderIssueModal from './OrderIssueModal.vue';
import OrderIssueTagSettingsModal from './OrderIssueTagSettingsModal.vue';
import MetricCategoriesModal from './MetricCategoriesModal.vue';
import TagManagementModal from './TagManagementModal.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import DeleteModal from 'dashboard/components/widgets/modal/DeleteModal.vue';

const props = defineProps({
  type: {
    type: String,
    default: 'active',
    validator: value => ['active', 'archive'].includes(value),
  },
});

const { t } = useI18n();
const store = useStore();
const route = useRoute();

const orderIssues = ref([]);
const loading = ref(false);
const selectedIssue = ref(null);
const showModal = ref(false);
const editingIssue = ref(null);
const showDeleteModal = ref(false);
const issueToDelete = ref(null);
const showTagSettingsModal = ref(false);
const showMetricCategoriesModal = ref(false);
const showTagManagementModal = ref(false);

// Filter state - Sets contain EXCLUDED tags (unchecked = excluded)
const excludedProblemTags = ref(new Set());
const excludedActionTags = ref(new Set());
const searchQuery = ref('');
const allProblemTags = ref([]);
const allActionTags = ref([]);
const tagColors = ref({ problem: {}, action: {} });

// Filter dropdown visibility
const showProblemFilter = ref(false);
const showActionFilter = ref(false);
const showVisibilityFilter = ref(false);
const showDateFilter = ref(false);

// Visibility filter state - which visibility states to show (checked = show)
const showVisible = ref(true);
const showHidden = ref(true);

// Date filter state
const dateFrom = ref('');
const dateTo = ref('');

const isArchiveView = computed(() => props.type === 'archive');

const pageTitle = computed(() =>
  isArchiveView.value
    ? t('ORDER_ISSUES.ARCHIVE_TITLE')
    : t('ORDER_ISSUES.TITLE')
);

const emptyMessage = computed(() =>
  isArchiveView.value
    ? t('ORDER_ISSUES.EMPTY_ARCHIVE')
    : t('ORDER_ISSUES.EMPTY_LIST')
);

const fetchOrderIssues = async () => {
  loading.value = true;
  try {
    // Always fetch all, filter client-side for visibility
    const response = isArchiveView.value
      ? await orderIssuesAPI.getArchived(null)
      : await orderIssuesAPI.getActive(null);
    orderIssues.value = response.data || [];
    
    // Extract unique tags from fetched issues
    extractTags();
  } catch (error) {
    useAlert(t('ORDER_ISSUES.API.FETCH_ERROR'));
    orderIssues.value = [];
  } finally {
    loading.value = false;
  }
};

const fetchTagColors = async () => {
  try {
    const response = await orderIssuesAPI.getTagSettings();
    const data = response.data;
    
    // Convert to object format for easy lookup
    const problemColors = {};
    const actionColors = {};
    
    if (data.problem_tags) {
      data.problem_tags.forEach(tag => {
        if (tag.color) {
          problemColors[tag.tag_name] = tag.color;
        }
      });
    }
    
    if (data.action_tags) {
      data.action_tags.forEach(tag => {
        if (tag.color) {
          actionColors[tag.tag_name] = tag.color;
        }
      });
    }
    
    tagColors.value = { problem: problemColors, action: actionColors };
  } catch (error) {
    console.error('Failed to fetch tag colors:', error);
  }
};

const extractTags = () => {
  const problemTags = new Set();
  const actionTags = new Set();
  
  orderIssues.value.forEach(issue => {
    if (issue.problem_tag) problemTags.add(issue.problem_tag);
    if (issue.action_tag) actionTags.add(issue.action_tag);
  });
  
  const newProblemTags = Array.from(problemTags).sort();
  const newActionTags = Array.from(actionTags).sort();
  
  allProblemTags.value = newProblemTags;
  allActionTags.value = newActionTags;
  
  // Clean up excluded tags that no longer exist
  excludedProblemTags.value = new Set(
    [...excludedProblemTags.value].filter(tag => newProblemTags.includes(tag))
  );
  excludedActionTags.value = new Set(
    [...excludedActionTags.value].filter(tag => newActionTags.includes(tag))
  );
};

const filteredOrderIssues = computed(() => {
  let filtered = orderIssues.value;
  
  // Filter by visibility (hidden/visible)
  if (!showVisible.value || !showHidden.value) {
    filtered = filtered.filter(issue => {
      if (issue.hidden) return showHidden.value;
      return showVisible.value;
    });
  }
  
  // Filter by problem tags - exclude unchecked tags
  if (excludedProblemTags.value.size > 0) {
    filtered = filtered.filter(issue => 
      !issue.problem_tag || !excludedProblemTags.value.has(issue.problem_tag)
    );
  }
  
  // Filter by action tags - exclude unchecked tags
  if (excludedActionTags.value.size > 0) {
    filtered = filtered.filter(issue => 
      !issue.action_tag || !excludedActionTags.value.has(issue.action_tag)
    );
  }
  
  // Filter by search query (order number or note)
  if (searchQuery.value.trim()) {
    const query = searchQuery.value.toLowerCase();
    filtered = filtered.filter(issue => {
      const orderNumber = (issue.order_number || '').toLowerCase();
      const note = (issue.note || '').toLowerCase();
      return orderNumber.includes(query) || note.includes(query);
    });
  }
  
  // Filter by date range
  if (dateFrom.value || dateTo.value) {
    filtered = filtered.filter(issue => {
      const issueDate = new Date(issue.created_at);
      issueDate.setHours(0, 0, 0, 0);
      
      if (dateFrom.value) {
        const fromDate = new Date(dateFrom.value);
        fromDate.setHours(0, 0, 0, 0);
        if (issueDate < fromDate) return false;
      }
      
      if (dateTo.value) {
        const toDate = new Date(dateTo.value);
        toDate.setHours(23, 59, 59, 999);
        if (issueDate > toDate) return false;
      }
      
      return true;
    });
  }
  
  return filtered;
});

const clearFilters = () => {
  excludedProblemTags.value = new Set();
  excludedActionTags.value = new Set();
  searchQuery.value = '';
  showVisible.value = true;
  showHidden.value = true;
  dateFrom.value = '';
  dateTo.value = '';
};

const hasActiveFilters = computed(() => {
  return excludedProblemTags.value.size > 0 || 
         excludedActionTags.value.size > 0 || 
         searchQuery.value.trim() || 
         !showVisible.value || 
         !showHidden.value ||
         dateFrom.value ||
         dateTo.value;
});

// Date filter helpers
const dateFilterActive = computed(() => dateFrom.value || dateTo.value);
const dateFilterLabel = computed(() => {
  if (dateFrom.value && dateTo.value) {
    return `${dateFrom.value} - ${dateTo.value}`;
  }
  if (dateFrom.value) {
    return `From ${dateFrom.value}`;
  }
  if (dateTo.value) {
    return `Until ${dateTo.value}`;
  }
  return '';
});
const clearDateFilter = () => {
  dateFrom.value = '';
  dateTo.value = '';
};

// Visibility filter helpers
const visibilityFilterActive = computed(() => !showVisible.value || !showHidden.value);
const selectAllVisibility = () => {
  showVisible.value = true;
  showHidden.value = true;
};
const deselectAllVisibility = () => {
  showVisible.value = false;
  showHidden.value = false;
};

// Tag filter helpers
const toggleProblemTag = (tag) => {
  const newSet = new Set(excludedProblemTags.value);
  if (newSet.has(tag)) {
    newSet.delete(tag);
  } else {
    newSet.add(tag);
  }
  excludedProblemTags.value = newSet;
};

const toggleActionTag = (tag) => {
  const newSet = new Set(excludedActionTags.value);
  if (newSet.has(tag)) {
    newSet.delete(tag);
  } else {
    newSet.add(tag);
  }
  excludedActionTags.value = newSet;
};

const selectAllProblemTags = () => {
  excludedProblemTags.value = new Set();
};

const deselectAllProblemTags = () => {
  excludedProblemTags.value = new Set(allProblemTags.value);
};

const selectAllActionTags = () => {
  excludedActionTags.value = new Set();
};

const deselectAllActionTags = () => {
  excludedActionTags.value = new Set(allActionTags.value);
};

const isProblemTagSelected = (tag) => !excludedProblemTags.value.has(tag);
const isActionTagSelected = (tag) => !excludedActionTags.value.has(tag);

const problemTagsSelectedCount = computed(() => 
  allProblemTags.value.length - excludedProblemTags.value.size
);
const actionTagsSelectedCount = computed(() => 
  allActionTags.value.length - excludedActionTags.value.size
);

const allProblemTagsSelected = computed(() => excludedProblemTags.value.size === 0);
const allActionTagsSelected = computed(() => excludedActionTags.value.size === 0);

const toggleHidden = async (issue) => {
  try {
    const newHiddenState = !issue.hidden;
    await orderIssuesAPI.toggleHidden(issue.id, newHiddenState);
    
    // Update in place - filtering is done client-side
    issue.hidden = newHiddenState;
    
    useAlert(newHiddenState ? t('ORDER_ISSUES.API.HIDE_SUCCESS') : t('ORDER_ISSUES.API.SHOW_SUCCESS'));
  } catch (error) {
    useAlert(t('ORDER_ISSUES.API.UPDATE_ERROR'));
  }
};

const toggleDone = async (issue) => {
  try {
    // Optimistically remove from list immediately
    const index = orderIssues.value.findIndex(i => i.id === issue.id);
    if (index !== -1) {
      orderIssues.value.splice(index, 1);
    }
    
    if (isArchiveView.value) {
      await orderIssuesAPI.unarchive(issue.id);
      useAlert(t('ORDER_ISSUES.API.UNARCHIVE_SUCCESS'));
    } else {
      await orderIssuesAPI.markAsDone(issue.id);
      useAlert(t('ORDER_ISSUES.API.ARCHIVE_SUCCESS'));
    }
    
    // Update store count without refetching
    store.dispatch('orderIssues/get');
  } catch (error) {
    // If error, add it back to the list
    if (index !== -1) {
      orderIssues.value.splice(index, 0, issue);
    }
    useAlert(t('ORDER_ISSUES.API.UPDATE_ERROR'));
  }
};

const updateNote = async (issue, newNote) => {
  try {
    const response = await orderIssuesAPI.update(issue.id, {
      order_issue: { note: newNote },
    });
    
    // Update the issue with the response data including updated_by_user
    if (response.data) {
      Object.assign(issue, response.data);
    } else {
      issue.note = newNote;
    }
    
    useAlert(t('ORDER_ISSUES.API.UPDATE_SUCCESS'));
  } catch (error) {
    useAlert(t('ORDER_ISSUES.API.UPDATE_ERROR'));
  }
};

const openConversation = (issue) => {
  const url = `/app/accounts/${store.getters.getCurrentAccountId}/conversations/${issue.conversation.display_id}`;
  window.open(url, '_blank', 'noopener,noreferrer');
};

const openCreateModal = () => {
  editingIssue.value = null;
  showModal.value = true;
};

const openEditModal = (issue) => {
  console.log('✏️ openEditModal called with issue:', issue);
  // Create a deep copy to avoid reactivity issues
  editingIssue.value = JSON.parse(JSON.stringify(issue));
  console.log('✏️ editingIssue.value set to:', editingIssue.value);
  showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
  editingIssue.value = null;
};

const handleModalSuccess = () => {
  fetchOrderIssues();
};

const openDeleteModal = (issue) => {
  issueToDelete.value = issue;
  showDeleteModal.value = true;
};

const closeDeleteModal = () => {
  showDeleteModal.value = false;
  issueToDelete.value = null;
};

const confirmDelete = async () => {
  if (!issueToDelete.value) return;
  
  try {
    await orderIssuesAPI.delete(issueToDelete.value.id);
    useAlert(t('ORDER_ISSUES.API.DELETE_SUCCESS'));
    await fetchOrderIssues();
    store.dispatch('orderIssues/get'); // Update count badge
  } catch (error) {
    useAlert(t('ORDER_ISSUES.API.DELETE_ERROR'));
  } finally {
    closeDeleteModal();
  }
};

const formatDate = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const getInitials = (name) => {
  if (!name) return '?';
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const openTagSettingsModal = () => {
  showTagSettingsModal.value = true;
};

const closeTagSettingsModal = () => {
  showTagSettingsModal.value = false;
};

const handleTagSettingsSuccess = () => {
  fetchTagColors();
};

const getTagStyle = (tagName, tagType) => {
  const color = tagColors.value[tagType]?.[tagName];
  if (!color) {
    return {};
  }
  return {
    backgroundColor: color,
    color: getContrastColor(color),
  };
};

const getContrastColor = (hexColor) => {
  // Convert hex to RGB
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);
  
  // Calculate luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  // Return black or white based on luminance
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
};

// Watch for route changes to refresh data
watch(() => props.type, () => {
  fetchOrderIssues();
  store.dispatch('orderIssues/get'); // Update count badge
});

onMounted(() => {
  fetchOrderIssues();
  fetchTagColors();
});
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <header class="sticky top-0 z-10 px-6">
      <div class="w-full max-w-[80rem] mx-auto">
        <div class="flex items-center justify-between w-full h-20 gap-2">
          <span class="text-xl font-medium text-n-slate-12">
            {{ pageTitle }}
          </span>
          <div v-if="!isArchiveView" class="flex items-center gap-2">
            <button
              class="flex items-center justify-center w-9 h-9 text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-1 rounded-md transition-colors"
              :title="$t('ORDER_ISSUES.TAG_MANAGEMENT.TITLE')"
              @click="showTagManagementModal = true"
            >
              <i class="i-lucide-tags text-base" />
            </button>
            <button
              class="flex items-center justify-center w-9 h-9 text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-1 rounded-md transition-colors"
              :title="$t('ORDER_ISSUES.METRIC_CATEGORIES.TITLE')"
              @click="showMetricCategoriesModal = true"
            >
              <i class="i-lucide-chart-bar text-base" />
            </button>
            <button
              class="flex items-center justify-center w-9 h-9 text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-1 rounded-md transition-colors"
              :title="$t('ORDER_ISSUES.TAG_SETTINGS.TITLE')"
              @click="openTagSettingsModal"
            >
              <i class="i-lucide-settings text-base" />
            </button>
            <Button
              :label="$t('ORDER_ISSUES.MODAL.CREATE_TITLE')"
              icon="i-lucide-plus"
              size="sm"
              @click="openCreateModal"
            />
          </div>
        </div>
      </div>
    </header>

    <main class="flex-1 px-6 overflow-y-auto">
      <div class="w-full max-w-[80rem] mx-auto py-4">
        <!-- Filter Bar -->
        <div class="mb-4 bg-n-solid-1 border border-n-weak rounded-lg">
          <!-- Main Filter Row -->
          <div class="flex items-center gap-3 p-3">
            <!-- Search Input -->
            <div class="flex-1 min-w-[200px] max-w-md">
              <input
                v-model="searchQuery"
                type="text"
                class="no-margin w-full h-9 px-3 text-sm border border-n-weak rounded-md bg-n-background text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-brand"
                :placeholder="$t('ORDER_ISSUES.FILTERS.SEARCH_PLACEHOLDER')"
              />
            </div>
            
            <!-- Filter Buttons Group -->
            <div class="flex items-center gap-2">
              <!-- Problem Tag Filter Dropdown -->
              <div v-if="allProblemTags.length > 0" class="relative">
                <button
                  class="flex items-center gap-1.5 h-9 px-3 text-sm border rounded-md transition-colors whitespace-nowrap"
                  :class="excludedProblemTags.size > 0 
                    ? 'bg-n-brand/10 border-n-brand text-n-brand' 
                    : 'bg-n-background border-n-weak text-n-slate-12 hover:bg-n-alpha-1'"
                  @click="showProblemFilter = !showProblemFilter"
                >
                  <i class="i-lucide-tag text-sm flex-shrink-0" />
                  <span>{{ $t('ORDER_ISSUES.FILTERS.PROBLEM') }}</span>
                  <i class="i-lucide-chevron-down text-xs flex-shrink-0" />
                </button>
                
                <!-- Problem Tags Dropdown -->
                <div
                  v-if="showProblemFilter"
                  class="absolute top-full left-0 mt-1 w-64 bg-n-solid-1 border border-n-weak rounded-lg shadow-lg z-30"
                >
                  <div class="flex items-center justify-between p-2 border-b border-n-weak">
                    <span class="text-xs font-medium text-n-slate-11">{{ $t('ORDER_ISSUES.FILTERS.PROBLEM_TAGS') }}</span>
                    <div class="flex items-center gap-1">
                      <button
                        class="px-2 py-1 text-xs text-n-brand hover:bg-n-alpha-1 rounded transition-colors"
                        @click="selectAllProblemTags"
                      >
                        {{ $t('ORDER_ISSUES.FILTERS.SELECT_ALL') }}
                      </button>
                      <span class="text-n-slate-9">|</span>
                      <button
                        class="px-2 py-1 text-xs text-n-slate-11 hover:bg-n-alpha-1 rounded transition-colors"
                        @click="deselectAllProblemTags"
                      >
                        {{ $t('ORDER_ISSUES.FILTERS.DESELECT_ALL') }}
                      </button>
                    </div>
                  </div>
                  <div class="max-h-64 overflow-y-auto p-1">
                    <label
                      v-for="tag in allProblemTags"
                      :key="tag"
                      class="flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-n-alpha-1 transition-colors"
                    >
                      <input
                        type="checkbox"
                        :checked="isProblemTagSelected(tag)"
                        class="w-4 h-4 rounded border-n-weak accent-n-brand cursor-pointer flex-shrink-0"
                        @change="toggleProblemTag(tag)"
                      />
                      <span
                        class="px-2 py-0.5 text-xs rounded-full truncate"
                        :style="{
                          backgroundColor: tagColors.problem[tag] || '#e2e8f0',
                          color: getContrastColor(tagColors.problem[tag] || '#e2e8f0')
                        }"
                      >
                        {{ tag }}
                      </span>
                    </label>
                  </div>
                  <div class="p-2 border-t border-n-weak">
                    <button
                      class="w-full px-3 py-1.5 text-sm font-medium text-n-slate-12 bg-n-alpha-1 hover:bg-n-alpha-2 rounded transition-colors"
                      @click="showProblemFilter = false"
                    >
                      {{ $t('ORDER_ISSUES.FILTERS.DONE') }}
                    </button>
                  </div>
                </div>
              </div>
              
              <!-- Action Tag Filter Dropdown -->
              <div v-if="allActionTags.length > 0" class="relative">
                <button
                  class="flex items-center gap-1.5 h-9 px-3 text-sm border rounded-md transition-colors whitespace-nowrap"
                  :class="excludedActionTags.size > 0 
                    ? 'bg-n-brand/10 border-n-brand text-n-brand' 
                    : 'bg-n-background border-n-weak text-n-slate-12 hover:bg-n-alpha-1'"
                  @click="showActionFilter = !showActionFilter"
                >
                  <i class="i-lucide-zap text-sm flex-shrink-0" />
                  <span>{{ $t('ORDER_ISSUES.FILTERS.ACTION') }}</span>
                  <i class="i-lucide-chevron-down text-xs flex-shrink-0" />
                </button>
                
                <!-- Action Tags Dropdown -->
                <div
                  v-if="showActionFilter"
                  class="absolute top-full left-0 mt-1 w-64 bg-n-solid-1 border border-n-weak rounded-lg shadow-lg z-30"
                >
                  <div class="flex items-center justify-between p-2 border-b border-n-weak">
                    <span class="text-xs font-medium text-n-slate-11">{{ $t('ORDER_ISSUES.FILTERS.ACTION_TAGS') }}</span>
                    <div class="flex items-center gap-1">
                      <button
                        class="px-2 py-1 text-xs text-n-brand hover:bg-n-alpha-1 rounded transition-colors"
                        @click="selectAllActionTags"
                      >
                        {{ $t('ORDER_ISSUES.FILTERS.SELECT_ALL') }}
                      </button>
                      <span class="text-n-slate-9">|</span>
                      <button
                        class="px-2 py-1 text-xs text-n-slate-11 hover:bg-n-alpha-1 rounded transition-colors"
                        @click="deselectAllActionTags"
                      >
                        {{ $t('ORDER_ISSUES.FILTERS.DESELECT_ALL') }}
                      </button>
                    </div>
                  </div>
                  <div class="max-h-64 overflow-y-auto p-1">
                    <label
                      v-for="tag in allActionTags"
                      :key="tag"
                      class="flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-n-alpha-1 transition-colors"
                    >
                      <input
                        type="checkbox"
                        :checked="isActionTagSelected(tag)"
                        class="w-4 h-4 rounded border-n-weak accent-n-brand cursor-pointer flex-shrink-0"
                        @change="toggleActionTag(tag)"
                      />
                      <span
                        class="px-2 py-0.5 text-xs rounded-full truncate"
                        :style="{
                          backgroundColor: tagColors.action[tag] || '#e2e8f0',
                          color: getContrastColor(tagColors.action[tag] || '#e2e8f0')
                        }"
                      >
                        {{ tag }}
                      </span>
                    </label>
                  </div>
                  <div class="p-2 border-t border-n-weak">
                    <button
                      class="w-full px-3 py-1.5 text-sm font-medium text-n-slate-12 bg-n-alpha-1 hover:bg-n-alpha-2 rounded transition-colors"
                      @click="showActionFilter = false"
                    >
                      {{ $t('ORDER_ISSUES.FILTERS.DONE') }}
                    </button>
                  </div>
                </div>
              </div>
              
              <!-- Visibility Filter Dropdown -->
              <div class="relative">
                <button
                  class="flex items-center gap-1.5 h-9 px-3 text-sm border rounded-md transition-colors whitespace-nowrap"
                  :class="visibilityFilterActive 
                    ? 'bg-n-brand/10 border-n-brand text-n-brand' 
                    : 'bg-n-background border-n-weak text-n-slate-12 hover:bg-n-alpha-1'"
                  @click="showVisibilityFilter = !showVisibilityFilter"
                >
                  <i class="i-lucide-eye text-sm flex-shrink-0" />
                  <span>{{ $t('ORDER_ISSUES.FILTERS.VISIBILITY') }}</span>
                  <i class="i-lucide-chevron-down text-xs flex-shrink-0" />
                </button>
                
                <!-- Visibility Dropdown -->
                <div
                  v-if="showVisibilityFilter"
                  class="absolute top-full right-0 mt-1 w-48 bg-n-solid-1 border border-n-weak rounded-lg shadow-lg z-30"
                >
                  <div class="flex items-center justify-between p-2 border-b border-n-weak">
                    <span class="text-xs font-medium text-n-slate-11">{{ $t('ORDER_ISSUES.FILTERS.VISIBILITY') }}</span>
                    <div class="flex items-center gap-1">
                      <button
                        class="px-2 py-1 text-xs text-n-brand hover:bg-n-alpha-1 rounded transition-colors"
                        @click="selectAllVisibility"
                      >
                        {{ $t('ORDER_ISSUES.FILTERS.SELECT_ALL') }}
                      </button>
                      <span class="text-n-slate-9">|</span>
                      <button
                        class="px-2 py-1 text-xs text-n-slate-11 hover:bg-n-alpha-1 rounded transition-colors"
                        @click="deselectAllVisibility"
                      >
                        {{ $t('ORDER_ISSUES.FILTERS.DESELECT_ALL') }}
                      </button>
                    </div>
                  </div>
                  <div class="p-1">
                    <label class="flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-n-alpha-1 transition-colors">
                      <input
                        v-model="showVisible"
                        type="checkbox"
                        class="w-4 h-4 rounded border-n-weak accent-n-brand cursor-pointer flex-shrink-0"
                      />
                      <span class="text-sm text-n-slate-12">{{ $t('ORDER_ISSUES.FILTERS.VISIBLE_ISSUES') }}</span>
                    </label>
                    <label class="flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-n-alpha-1 transition-colors">
                      <input
                        v-model="showHidden"
                        type="checkbox"
                        class="w-4 h-4 rounded border-n-weak accent-n-brand cursor-pointer flex-shrink-0"
                      />
                      <span class="text-sm text-n-slate-12">{{ $t('ORDER_ISSUES.FILTERS.HIDDEN_ISSUES') }}</span>
                    </label>
                  </div>
                  <div class="p-2 border-t border-n-weak">
                    <button
                      class="w-full px-3 py-1.5 text-sm font-medium text-n-slate-12 bg-n-alpha-1 hover:bg-n-alpha-2 rounded transition-colors"
                      @click="showVisibilityFilter = false"
                    >
                      {{ $t('ORDER_ISSUES.FILTERS.DONE') }}
                    </button>
                  </div>
                </div>
              </div>
              
              <!-- Date Filter Dropdown -->
              <div class="relative">
                <button
                  class="flex items-center gap-1.5 h-9 px-3 text-sm border rounded-md transition-colors whitespace-nowrap"
                  :class="dateFilterActive 
                    ? 'bg-n-brand/10 border-n-brand text-n-brand' 
                    : 'bg-n-background border-n-weak text-n-slate-12 hover:bg-n-alpha-1'"
                  @click="showDateFilter = !showDateFilter"
                >
                  <i class="i-lucide-calendar text-sm flex-shrink-0" />
                  <span>{{ $t('ORDER_ISSUES.FILTERS.DATE') }}</span>
                  <i class="i-lucide-chevron-down text-xs flex-shrink-0" />
                </button>
                
                <!-- Date Dropdown -->
                <div
                  v-if="showDateFilter"
                  class="absolute top-full right-0 mt-1 w-72 bg-n-solid-1 border border-n-weak rounded-lg shadow-lg z-30"
                >
                  <div class="flex items-center justify-between p-2 border-b border-n-weak">
                    <span class="text-xs font-medium text-n-slate-11">{{ $t('ORDER_ISSUES.FILTERS.DATE_RANGE') }}</span>
                    <button
                      v-if="dateFilterActive"
                      class="px-2 py-1 text-xs text-ruby-11 hover:bg-ruby-3 rounded transition-colors"
                      @click="clearDateFilter"
                    >
                      {{ $t('ORDER_ISSUES.FILTERS.CLEAR_DATE') }}
                    </button>
                  </div>
                  <div class="p-3 space-y-3">
                    <div>
                      <label class="block text-xs font-medium text-n-slate-11 mb-1">
                        {{ $t('ORDER_ISSUES.FILTERS.FROM') }}
                      </label>
                      <input
                        v-model="dateFrom"
                        type="date"
                        class="no-margin w-full h-9 px-3 text-sm border border-n-weak rounded-md bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
                      />
                    </div>
                    <div>
                      <label class="block text-xs font-medium text-n-slate-11 mb-1">
                        {{ $t('ORDER_ISSUES.FILTERS.TO') }}
                      </label>
                      <input
                        v-model="dateTo"
                        type="date"
                        class="no-margin w-full h-9 px-3 text-sm border border-n-weak rounded-md bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
                      />
                    </div>
                  </div>
                  <div class="p-2 border-t border-n-weak">
                    <button
                      class="w-full px-3 py-1.5 text-sm font-medium text-n-slate-12 bg-n-alpha-1 hover:bg-n-alpha-2 rounded transition-colors"
                      @click="showDateFilter = false"
                    >
                      {{ $t('ORDER_ISSUES.FILTERS.DONE') }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Clear Filters Button -->
            <button
              v-if="hasActiveFilters"
              class="h-9 px-3 text-sm font-medium text-ruby-11 hover:text-ruby-12 hover:bg-ruby-3 rounded-md transition-colors whitespace-nowrap flex items-center gap-1"
              @click="clearFilters"
            >
              <i class="i-lucide-x text-sm" />
              <span>{{ $t('ORDER_ISSUES.FILTERS.CLEAR') }}</span>
            </button>
          </div>
          
          <!-- Results Count Bar - Always visible with consistent height -->
          <div class="flex items-center h-8 px-3 border-t border-n-weak bg-n-alpha-1 text-xs text-n-slate-11">
            <span v-if="hasActiveFilters">
              {{ $t('ORDER_ISSUES.FILTERS.SHOWING_RESULTS', { count: filteredOrderIssues.length, total: orderIssues.length }) }}
            </span>
            <span v-else>
              {{ orderIssues.length }} {{ orderIssues.length === 1 ? 'issue' : 'issues' }}
            </span>
          </div>
        </div>
        
        <!-- Click outside to close dropdowns -->
        <div
          v-if="showProblemFilter || showActionFilter || showVisibilityFilter || showDateFilter"
          class="fixed inset-0 z-20"
          @click="showProblemFilter = false; showActionFilter = false; showVisibilityFilter = false; showDateFilter = false"
        />

        <!-- Loading State -->
        <div v-if="loading" class="flex items-center justify-center py-10 text-n-slate-11">
          <Spinner />
        </div>

        <!-- Empty State -->
        <div
          v-else-if="!filteredOrderIssues.length"
          class="flex flex-col items-center justify-center py-20"
        >
          <EmptyState :message="hasActiveFilters ? $t('ORDER_ISSUES.FILTERS.NO_RESULTS') : emptyMessage" />
        </div>

        <!-- Order Issues Table -->
        <div v-else class="overflow-hidden rounded-lg border border-n-weak">
          <div class="overflow-x-auto overflow-y-auto max-h-[calc(100vh-150px)]">
            <table class="w-full min-w-[900px]">
              <thead class="bg-n-solid-2 sticky top-0 z-10">
                <tr class="border-b border-n-weak">
                  <th class="px-3 py-2 text-left text-xs font-medium text-n-slate-11 min-w-[80px]">
                    {{ $t('ORDER_ISSUES.TABLE.CONVERSATION_ID') }}
                  </th>
                  <th class="px-3 py-2 text-left text-xs font-medium text-n-slate-11 min-w-[100px]">
                    {{ $t('ORDER_ISSUES.TABLE.ORDER_NUMBER') }}
                  </th>
                  <th class="px-3 py-2 text-left text-xs font-medium text-n-slate-11 min-w-[120px]">
                    {{ $t('ORDER_ISSUES.TABLE.PROBLEM') }}
                  </th>
                  <th class="px-3 py-2 text-left text-xs font-medium text-n-slate-11 min-w-[120px]">
                    {{ $t('ORDER_ISSUES.TABLE.ACTION') }}
                  </th>
                  <th class="px-3 py-2 text-left text-xs font-medium text-n-slate-11 min-w-[200px]">
                    {{ $t('ORDER_ISSUES.TABLE.NOTE') }}
                  </th>
                  <th class="px-3 py-2 text-left text-xs font-medium text-n-slate-11 min-w-[60px]">
                    {{ $t('ORDER_ISSUES.TABLE.CONVERSATION') }}
                  </th>
                  <th class="px-3 py-2 text-left text-xs font-medium text-n-slate-11 min-w-[160px]">
                    {{ $t('ORDER_ISSUES.TABLE.CREATED_BY') }}
                  </th>
                  <th class="px-3 py-2 text-left text-xs font-medium text-n-slate-11 min-w-[160px]">
                    {{ $t('ORDER_ISSUES.TABLE.UPDATED_BY') }}
                  </th>
                  <th class="px-3 py-2 text-center text-xs font-medium text-n-slate-11 min-w-[60px]">
                    {{ $t('ORDER_ISSUES.TABLE.DONE') }}
                  </th>
                  <th class="px-3 py-2 text-center text-xs font-medium text-n-slate-11 min-w-[80px]">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody class="bg-n-solid-1">
                <tr
                  v-for="issue in filteredOrderIssues"
                  :key="issue.id"
                  class="border-b border-n-weak last:border-b-0 hover:bg-n-alpha-1 dark:hover:bg-n-alpha-2 transition-colors"
                >
                  <td class="px-3 py-2 align-middle">
                    <span class="text-xs text-n-slate-11">#{{ issue.conversation?.display_id || issue.conversation_id }}</span>
                  </td>
                  <td class="px-3 py-2 align-middle">
                    <span class="text-xs text-n-slate-12">{{ issue.order_number }}</span>
                  </td>
                  <td class="px-3 py-2 align-middle">
                    <span
                      v-if="issue.problem_tag"
                      class="inline-flex items-center px-2 py-0.5 rounded text-xs"
                      :style="getTagStyle(issue.problem_tag, 'problem')"
                      :class="!tagColors.problem[issue.problem_tag] ? 'text-n-slate-12 bg-n-alpha-2' : ''"
                    >
                      {{ issue.problem_tag }}
                    </span>
                    <span v-else class="text-n-slate-10 text-xs">—</span>
                  </td>
                  <td class="px-3 py-2 align-middle">
                    <span
                      v-if="issue.action_tag"
                      class="inline-flex items-center px-2 py-0.5 rounded text-xs"
                      :style="getTagStyle(issue.action_tag, 'action')"
                      :class="!tagColors.action[issue.action_tag] ? 'text-n-slate-12 bg-n-alpha-2' : ''"
                    >
                      {{ issue.action_tag }}
                    </span>
                    <span v-else class="text-n-slate-10 text-xs">—</span>
                  </td>
                  <td class="px-3 py-2 align-middle">
                    <input
                      :value="issue.note"
                      type="text"
                      class="w-full px-2 py-1 !mb-0 !text-xs border border-n-weak rounded bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
                      :placeholder="$t('ORDER_ISSUES.TABLE.NOTE_PLACEHOLDER')"
                      @blur="updateNote(issue, $event.target.value)"
                    />
                  </td>
                  <td class="px-3 py-2 align-middle">
                    <button
                      class="text-n-brand hover:underline text-xs"
                      @click="openConversation(issue)"
                    >
                      <i class="i-lucide-external-link text-xs" />
                    </button>
                  </td>
                  <td class="px-3 py-2 align-middle">
                    <div class="flex items-center gap-2">
                      <!-- Profile Picture or Initials -->
                      <div v-if="issue.user" class="flex-shrink-0 relative w-6 h-6">
                        <!-- Initials as fallback (always rendered behind) -->
                        <div
                          class="absolute inset-0 w-6 h-6 rounded-full bg-n-brand text-white flex items-center justify-center text-[10px] font-medium"
                        >
                          {{ getInitials(issue.user.name) }}
                        </div>
                        <!-- Avatar image on top -->
                        <img
                          v-if="issue.user.avatar_url && issue.user.avatar_url !== 'null'"
                          :src="issue.user.avatar_url"
                          :alt="issue.user.name"
                          class="absolute inset-0 w-6 h-6 rounded-full object-cover"
                          @error="$event.target.style.display='none'"
                        />
                      </div>
                      <div v-else class="flex-shrink-0">
                        <div class="w-6 h-6 rounded-full bg-n-slate-6 flex items-center justify-center">
                          <i class="i-lucide-user text-xs text-n-slate-11" />
                        </div>
                      </div>
                      
                      <!-- Name and Time -->
                      <div class="flex flex-col min-w-0">
                        <span class="text-xs text-n-slate-12 truncate">
                          {{ issue.user?.name || 'Unknown' }}
                        </span>
                        <span class="text-[10px] text-n-slate-10">
                          {{ formatDate(issue.created_at) }}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td class="px-3 py-2 align-middle">
                    <div class="flex items-center gap-2">
                      <!-- Profile Picture or Initials -->
                      <div v-if="issue.updated_by_user" class="flex-shrink-0 relative w-6 h-6">
                        <!-- Initials as fallback (always rendered behind) -->
                        <div
                          class="absolute inset-0 w-6 h-6 rounded-full bg-n-brand text-white flex items-center justify-center text-[10px] font-medium"
                        >
                          {{ getInitials(issue.updated_by_user.name) }}
                        </div>
                        <!-- Avatar image on top -->
                        <img
                          v-if="issue.updated_by_user.avatar_url && issue.updated_by_user.avatar_url !== 'null'"
                          :src="issue.updated_by_user.avatar_url"
                          :alt="issue.updated_by_user.name"
                          class="absolute inset-0 w-6 h-6 rounded-full object-cover"
                          @error="$event.target.style.display='none'"
                        />
                      </div>
                      <div v-else class="flex-shrink-0">
                        <div class="w-6 h-6 rounded-full bg-n-slate-6 flex items-center justify-center">
                          <i class="i-lucide-minus text-xs text-n-slate-11" />
                        </div>
                      </div>
                      
                      <!-- Name and Time -->
                      <div class="flex flex-col min-w-0">
                        <span class="text-xs text-n-slate-12 truncate">
                          {{ issue.updated_by_user?.name || '—' }}
                        </span>
                        <span class="text-[10px] text-n-slate-10">
                          {{ issue.updated_at ? formatDate(issue.updated_at) : '—' }}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td class="px-3 py-2 align-middle text-center">
                    <input
                      :checked="issue.done"
                      type="checkbox"
                      class="w-3.5 h-3.5 cursor-pointer accent-n-brand"
                      @change="toggleDone(issue)"
                    />
                  </td>
                  <td class="px-3 py-2 align-middle text-center">
                    <div class="flex items-center justify-center gap-1">
                      <button
                        class="transition-colors p-1"
                        :class="issue.hidden 
                          ? 'text-amber-500 hover:text-amber-600' 
                          : 'text-n-slate-11 hover:text-n-slate-12'"
                        :title="issue.hidden ? $t('ORDER_ISSUES.ACTIONS.SHOW') : $t('ORDER_ISSUES.ACTIONS.HIDE')"
                        @click="toggleHidden(issue)"
                      >
                        <i :class="issue.hidden ? 'i-lucide-eye' : 'i-lucide-eye-off'" class="text-sm" />
                      </button>
                      <button
                        class="text-n-slate-11 hover:text-n-brand transition-colors p-1"
                        @click="openEditModal(issue)"
                      >
                        <i class="i-lucide-pencil text-sm" />
                      </button>
                      <button
                        class="text-n-slate-11 hover:text-red-500 transition-colors p-1"
                        @click="openDeleteModal(issue)"
                      >
                        <i class="i-lucide-trash-2 text-sm" />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </main>

    <!-- Create/Edit Modal -->
    <OrderIssueModal
      :show="showModal"
      :order-issue="editingIssue"
      @close="closeModal"
      @success="handleModalSuccess"
    />

    <!-- Delete Confirmation Modal -->
    <DeleteModal
      v-if="showDeleteModal"
      v-model:show="showDeleteModal"
      :title="$t('ORDER_ISSUES.DELETE.TITLE')"
      :message="$t('ORDER_ISSUES.DELETE.MESSAGE')"
      :message-value="issueToDelete?.order_number"
      :confirm-text="$t('ORDER_ISSUES.DELETE.CONFIRM')"
      :reject-text="$t('ORDER_ISSUES.DELETE.CANCEL')"
      :on-confirm="confirmDelete"
      :on-close="closeDeleteModal"
    />

    <!-- Tag Settings Modal -->
    <OrderIssueTagSettingsModal
      :show="showTagSettingsModal"
      :all-problem-tags="allProblemTags"
      :all-action-tags="allActionTags"
      @close="closeTagSettingsModal"
      @success="handleTagSettingsSuccess"
    />

    <!-- Metric Categories Modal -->
    <MetricCategoriesModal
      :show="showMetricCategoriesModal"
      @close="showMetricCategoriesModal = false"
      @success="showMetricCategoriesModal = false"
    />

    <!-- Tag Management Modal -->
    <TagManagementModal
      :show="showTagManagementModal"
      @close="showTagManagementModal = false"
    />
  </section>
</template>
